import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react';
import PlanUpgrade from '../PlanUpgrade';
import { SubscriptionFeatureId } from '../../../../state/useCustomerDetails';
import { Z_INDEX_MOBILE_OVERLAY } from '@upheal/design-system/components/Sidebar/constants';

type UpgradeDialogContextValue = {
    onStartUpgrade: (newFeatureId: SubscriptionFeatureId) => void;
    onStopUpgrade: () => void;
};

const UpgradeDialogContext = createContext<
    UpgradeDialogContextValue | undefined
>(undefined);

export const UpgradeDialogProvider = ({ children }: PropsWithChildren) => {
    const [featureId, setFeatureId] = useState<SubscriptionFeatureId | null>(
        null,
    );

    const onStartUpgrade = useCallback(
        (newFeatureId: SubscriptionFeatureId) => setFeatureId(newFeatureId),
        [],
    );
    const onStopUpgrade = useCallback(() => setFeatureId(null), []);

    return (
        <UpgradeDialogContext.Provider
            value={useMemo(
                () => ({
                    onStartUpgrade,
                    onStopUpgrade,
                }),
                [onStartUpgrade, onStopUpgrade],
            )}
        >
            {children}

            {featureId ? (
                <PlanUpgrade
                    featureId={featureId}
                    onClose={onStopUpgrade}
                    zIndex={Z_INDEX_MOBILE_OVERLAY + 1}
                />
            ) : null}
        </UpgradeDialogContext.Provider>
    );
};

export const useUpgradeDialogContext = () => {
    const value = useContext(UpgradeDialogContext);

    if (!value) {
        throw new Error(
            'useUpgradeDialogContext must be used within a UpgradeDialogProvider',
        );
    }

    return value;
};
